import { Card, CardContent } from '@/components/ui/card';
import { Circle, Award, Star, Gem, Diamond, Shield, CheckCircle, TrendingUp } from 'lucide-react';

var CIRCLE_ICONS = { circle: Circle, award: Award, star: Star, gem: Gem, diamond: Diamond };

var CIRCLE_GRADIENT = {
  bronze: 'from-amber-700/40 to-amber-700',
  silver: 'from-slate-400/40 to-slate-400',
  gold: 'from-yellow-500/40 to-yellow-500',
  platinum: 'from-slate-300/40 to-slate-300',
  diamond: 'from-cyan-300/40 to-cyan-300',
};

function CredibilityBreakdown(props) {
  var breakdown = props.breakdown;
  if (!breakdown) return null;
  var items = [
    { label: 'Trust', value: breakdown.trust, max: 350, color: 'bg-blue-500' },
    { label: 'Cycles', value: breakdown.cycles, max: 250, color: 'bg-emerald-500' },
    { label: 'On-time', value: breakdown.on_time, max: 250, color: 'bg-amber-500' },
    { label: 'Points', value: breakdown.points, max: 150, color: 'bg-violet-400' },
  ];
  if (breakdown.default_penalty > 0) {
    items.push({ label: 'Defaults', value: -breakdown.default_penalty, max: 0, color: 'bg-red-500' });
  }
  return (
    <div className="mt-3 space-y-1.5" data-testid="credibility-breakdown">
      {items.map(function(item) {
        var pct = item.max > 0 ? Math.min((item.value / item.max) * 100, 100) : 0;
        return (
          <div key={item.label} className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500 w-14">{item.label}</span>
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={'h-full rounded-full ' + item.color} style={{ width: pct + '%' }} />
            </div>
            <span className={'text-[10px] font-bold ' + (item.value < 0 ? 'text-red-500' : 'text-slate-700')}>
              {item.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function CircleProgress(props) {
  var profile = props.profile;
  if (!profile) return null;
  var current = profile.current_circle;
  var next = profile.next_circle;
  var pct = profile.progress_percentage;
  var toNext = profile.points_to_next;
  var credScore = profile.credibility_score || 0;
  var trustScore = profile.trust_score || 50;
  var onTimeRate = profile.on_time_rate || 100;
  var cycles = profile.completed_cycles || 0;
  var defaults = profile.defaults_count || 0;
  var Icon = CIRCLE_ICONS[current ? current.icon : 'circle'] || Circle;
  var gradientClass = current ? (CIRCLE_GRADIENT[current.id] || 'from-amber-700/40 to-amber-700') : '';

  return (
    <div className="space-y-3" data-testid="circle-progress-card">
      {/* Credibility Score Hero */}
      <Card className="glass-card overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-center gap-4 mb-4">
            <div className={'w-16 h-16 rounded-full flex items-center justify-center shadow-lg bg-gradient-to-br ' + gradientClass} data-testid="current-circle-badge">
              <Icon size={28} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="text-lg font-black text-slate-900" data-testid="circle-name">
                {current ? current.name : 'Bronze'} Circle
              </p>
              <p className="text-sm text-slate-500">Level {current ? current.level : 1}</p>
              <p className="text-xs font-bold mt-0.5 text-[#0B1F3A]">
                Credibility: {credScore}
              </p>
            </div>
          </div>

          {next && (
            <div data-testid="next-circle-progress">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-slate-600 font-medium">Progress to {next.name}</span>
                <span className="font-bold text-slate-800">{pct}%</span>
              </div>
              <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#0B1F3A] to-[#2272C5] transition-all duration-1000"
                  style={{ width: Math.min(pct, 100) + '%' }}
                />
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">
                {toNext} more credibility to {next.name}
              </p>
            </div>
          )}
          {!next && (
            <div className="text-center py-2">
              <p className="text-sm font-bold text-emerald-600">Maximum credibility level!</p>
            </div>
          )}

          <CredibilityBreakdown breakdown={profile.score_breakdown} />
        </CardContent>
      </Card>

      {/* Trust Metrics */}
      <div className="grid grid-cols-2 gap-2">
        <Card className="glass-card">
          <CardContent className="p-3 flex items-center gap-2">
            <Shield size={18} className="text-blue-600" />
            <div>
              <p className="text-lg font-black text-slate-900" data-testid="trust-score-display">{trustScore}</p>
              <p className="text-[10px] text-slate-500">Trust Score</p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-3 flex items-center gap-2">
            <CheckCircle size={18} className="text-emerald-600" />
            <div>
              <p className="text-lg font-black text-slate-900" data-testid="ontime-rate-display">{onTimeRate}%</p>
              <p className="text-[10px] text-slate-500">On-time Rate</p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-3 flex items-center gap-2">
            <TrendingUp size={18} className="text-amber-600" />
            <div>
              <p className="text-lg font-black text-slate-900">{cycles}</p>
              <p className="text-[10px] text-slate-500">Completed Cycles</p>
            </div>
          </CardContent>
        </Card>
        <Card className={'glass-card ' + (defaults > 0 ? 'border border-red-200' : '')}>
          <CardContent className="p-3 flex items-center gap-2">
            <Shield size={18} className={defaults > 0 ? 'text-red-500' : 'text-slate-400'} />
            <div>
              <p className={'text-lg font-black ' + (defaults > 0 ? 'text-red-500' : 'text-slate-900')}>{defaults}</p>
              <p className="text-[10px] text-slate-500">Defaults</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default CircleProgress;
